import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SeenLogo } from '@/components/common/SeenLogo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Users, 
  Shield, 
  Smartphone,
  Globe,
  CheckCircle,
  ArrowRight,
  Mail,
  Building2,
  PoundSterling,
  Zap,
  Heart,
  Store,
  QrCode,
  Scan,
  AlertTriangle,
  BarChart3,
  Target,
  Rocket,
  Award,
  ExternalLink
} from 'lucide-react';

const InvestorsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Thank you for your interest! We will be in touch within 48 hours.');
    setFormData({ name: '', email: '', company: '', message: '' });
    setSubmitting(false);
  };

  const stats = [
    { label: 'UK Food Allergy Sufferers', value: '2M+', icon: Users },
    { label: 'Market Size (UK)', value: '£1.2B', icon: PoundSterling },
    { label: 'Annual Growth', value: '8.5%', icon: TrendingUp },
    { label: 'Products in Database', value: '50K+', icon: BarChart3 },
  ];

  const features = [
    { 
      icon: Scan, 
      title: 'AI Barcode Scanner', 
      desc: 'Instant product analysis using GPT-5.2 and multi-source database lookup' 
    },
    { 
      icon: AlertTriangle, 
      title: 'Hidden Ingredient Detection', 
      desc: 'Exposes disguised additives like "natural flavors" containing MSG' 
    },
    { 
      icon: Store, 
      title: 'Supermarket QR System', 
      desc: 'Stores display QR codes letting customers find safe products instantly' 
    },
    { 
      icon: QrCode, 
      title: 'Festival Wristbands', 
      desc: 'Color-coded allergen wristbands for events with vendor integration' 
    },
    { 
      icon: Heart, 
      title: 'Hardship Access', 
      desc: 'Free access for those on benefits - social impact built into the model' 
    },
    { 
      icon: Globe, 
      title: 'B2B API Platform', 
      desc: 'White-label API for food chains, retailers, and third-party apps' 
    },
  ];

  const investors = [
    {
      type: 'Angel Networks',
      examples: ['UK Business Angels Association', 'Angel Investment Network', 'Seedrs', 'Crowdcube'],
      why: 'Food-tech and health-tech angels actively invest in consumer safety apps'
    },
    {
      type: 'Food-Tech VCs',
      examples: ['Five Seasons Ventures', 'Acre Venture Partners', 'Anterra Capital', 'Synthesis Capital'],
      why: 'Specialists in food innovation with deep industry connections'
    },
    {
      type: 'Health-Tech Funds',
      examples: ['Albion Capital', 'MMC Ventures', 'Oxford Capital', 'Episode 1'],
      why: 'Focus on consumer health and wellness applications'
    },
    {
      type: 'Impact Investors',
      examples: ['Big Society Capital', 'Nesta Impact', 'Social Tech Trust'],
      why: 'Our Hardship Access program aligns with social impact mandates'
    },
    {
      type: 'Accelerators',
      examples: ['Techstars London', 'Seedcamp', 'Entrepreneur First', 'Food-X'],
      why: 'Mentorship + funding + network for early-stage food-tech'
    },
    {
      type: 'Strategic Partners',
      examples: ['Supermarket chains', 'NHS Innovation', 'Allergy UK partnerships'],
      why: 'Industry players seeking innovation partnerships'
    }
  ];

  const traction = [
    'Working MVP with barcode scanning, AI analysis, and subscription billing',
    'Multi-source product database (Open Food Facts + 6 additional sources)',
    'Stripe integration with UK pricing (£19-£199/month tiers)',
    'Festival wristband system with PDF generation and vendor displays',
    'Supermarket QR feature for in-store safe product discovery',
    'GDPR compliant with full Terms of Service and Privacy Policy'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <SeenLogo size="sm" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-white/70 hover:text-white">
                  Back to App
                </Button>
              </Link>
              <a href="#contact">
                <Button className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                  Get in Touch
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 bg-[#5DBDB3]/20 text-[#5DBDB3] border-[#5DBDB3]/30 px-4 py-1">
            <Rocket className="w-3 h-3 mr-1" />
            Seeking Seed Investment
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Helping 2 Million UK Allergy
            <br />
            <span className="text-[#5DBDB3]">Sufferers Eat Safely</span>
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto">
            Seen Beyond the Label uses AI to expose hidden ingredients in food products, 
            helping people with allergies make safe choices. We&apos;re building the infrastructure 
            for allergen-safe shopping.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#demo">
              <Button size="lg" className="bg-[#5DBDB3] hover:bg-[#4AA89E] text-lg px-8">
                View Live Demo
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#contact">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8">
                Request Pitch Deck
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-[#5DBDB3] mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-white/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  The Problem
                </h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    2+ million people in the UK suffer from food allergies
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    10 deaths per year from allergic reactions to food in the UK
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    Labels use confusing names that hide allergens (e.g., casein = milk)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    Shopping takes 3x longer for allergy sufferers checking every label
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    No easy way to find safe products in supermarkets
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#5DBDB3]/10 border-[#5DBDB3]/30">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-[#5DBDB3]" />
                  Our Solution
                </h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DBDB3] mt-1">•</span>
                    Instant barcode scanning with AI-powered ingredient analysis
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DBDB3] mt-1">•</span>
                    Personalised allergen alerts based on user profile
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DBDB3] mt-1">•</span>
                    Hidden ingredient detection exposing disguised additives
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DBDB3] mt-1">•</span>
                    Supermarket QR codes showing all safe products in-store
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DBDB3] mt-1">•</span>
                    Festival wristband system for event allergen management
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Platform Features</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A comprehensive ecosystem for allergen safety across consumers, retailers, and events
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:border-[#5DBDB3]/50 transition-colors">
                <CardContent className="p-6">
                  <feature.icon className="w-10 h-10 text-[#5DBDB3] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Traction */}
      <section className="py-20 px-4" id="demo">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
              <Award className="w-3 h-3 mr-1" />
              Current Traction
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What We&apos;ve Built</h2>
          </div>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8">
              <ul className="space-y-4">
                {traction.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth">
                  <Button className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                    Try Live Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/store/demo-tesco-london/scan">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    View Store QR Demo
                    <QrCode className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Investor Types */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 border-purple-500/30">
              <Target className="w-3 h-3 mr-1" />
              Funding Strategy
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Best-Fit Investors</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We&apos;re seeking partners who understand food-tech, health-tech, or social impact
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investors.map((inv, index) => (
              <Card key={index} className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#5DBDB3] mb-3">{inv.type}</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {inv.examples.map((ex, i) => (
                      <Badge key={i} variant="secondary" className="bg-white/10 text-white/70 text-xs">
                        {ex}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-white/60 text-sm">{inv.why}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Model */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Revenue Streams</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <PoundSterling className="w-8 h-8 text-[#5DBDB3] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">B2C Subscriptions</h3>
                <p className="text-white/60 text-sm mb-3">
                  Individual users pay £19-£69/month for premium scanning and hidden ingredient detection
                </p>
                <p className="text-[#5DBDB3] font-semibold">£19 - £69/month</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <Building2 className="w-8 h-8 text-[#5DBDB3] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">B2B Enterprise</h3>
                <p className="text-white/60 text-sm mb-3">
                  Food chains and retailers pay for API access and store QR code system
                </p>
                <p className="text-[#5DBDB3] font-semibold">£149 - £199/month</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <QrCode className="w-8 h-8 text-[#5DBDB3] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Festival Licensing</h3>
                <p className="text-white/60 text-sm mb-3">
                  Event organisers license our wristband system for allergen management
                </p>
                <p className="text-[#5DBDB3] font-semibold">£149/month + per-event</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <Zap className="w-8 h-8 text-[#5DBDB3] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">API Marketplace</h3>
                <p className="text-white/60 text-sm mb-3">
                  Third-party apps integrate our ingredient analysis via pay-per-call API
                </p>
                <p className="text-[#5DBDB3] font-semibold">Usage-based pricing</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4 bg-white/5" id="contact">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Get in Touch</h2>
            <p className="text-white/60">
              Interested in learning more? Request our pitch deck or schedule a demo call.
            </p>
          </div>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">Your Name</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">Email Address</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">Company / Fund</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Venture Capital Ltd"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="I'd like to request your pitch deck and learn more about your funding round..."
                    rows={4}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#5DBDB3] hover:bg-[#4AA89E]"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                  <Mail className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-white/40 text-sm">
              Or email directly: <a href="mailto:invest@seenbeyondthelabel.com" className="text-[#5DBDB3] hover:underline">invest@seenbeyondthelabel.com</a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <SeenLogo size="sm" />
          <p className="text-white/40 text-sm">
            © 2026 Seen Beyond the Label. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to="/privacy" className="text-white/40 hover:text-white text-sm">Privacy</Link>
            <Link to="/terms" className="text-white/40 hover:text-white text-sm">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InvestorsPage;
